import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";

const SEEN_KEY = "vi_exit_intent_seen";
const SEEN_TTL_MS = 7 * 24 * 3600 * 1000;

const recentlySeen = () => {
  const t = Number(localStorage.getItem(SEEN_KEY) || 0);
  return t && Date.now() - t < SEEN_TTL_MS;
};

export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const armedAt = useRef(Date.now());

  useEffect(() => {
    if (recentlySeen()) return;
    api.get("/promo").then((r) => r.data.active && r.data.text && setPromo(r.data.text)).catch(() => {});
    const onLeave = (e) => {
      if (e.clientY > 0 || Date.now() - armedAt.current < 4000 || recentlySeen()) return;
      localStorage.setItem(SEEN_KEY, String(Date.now()));
      setOpen(true);
      api.post("/track/exit-intent", { event: "shown" }).catch(() => {});
    };
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => document.documentElement.removeEventListener("mouseleave", onLeave);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      api.post("/track/exit-intent", { event: "captured" }).catch(() => {});
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="exit-intent-popup">
        <DialogHeader>
          <div className="mx-auto h-12 w-12 rounded-xl bg-blue-600/20 flex items-center justify-center mb-1">
            <Gift className="h-6 w-6 text-blue-400" />
          </div>
          <DialogTitle className="font-head text-2xl font-extrabold text-center">
            Wait — leave with something free
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-center">
            Your free credits make a couple of full videos end-to-end. No card required.
          </DialogDescription>
        </DialogHeader>
        {promo && (
          <div className="border border-emerald-500/40 rounded-md bg-emerald-600/10 px-4 py-2.5 flex items-center gap-2" data-testid="exit-intent-promo">
            <Sparkles className="h-4 w-4 text-emerald-400 shrink-0" />
            <p className="text-sm"><span className="font-semibold text-emerald-300">This month's special:</span> <span className="text-zinc-200">{promo}</span></p>
          </div>
        )}
        <Link
          to="/register"
          data-testid="exit-intent-register-cta"
          className="block text-center bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors"
          onClick={() => setOpen(false)}
        >
          Claim my free credits
        </Link>
        {done ? (
          <p className="text-sm text-emerald-300 text-center" data-testid="exit-intent-done">
            You're in — the weekly guide lands in your inbox.
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <p className="text-xs text-zinc-500 text-center">Not ready yet? Get one practical faceless-video guide a week instead:</p>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="exit-intent-email-input"
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="exit-intent-subscribe-btn"
                className="bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {busy ? "…" : "Send it"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
